import { bisector } from "d3-array";
import type { ScaleLinear, ScaleTime } from "d3-scale";
import type { TooltipData } from "../context/ChartContext";
import type { BaseDataPoint } from "./charts";

const bisectDate = bisector<BaseDataPoint, Date>((d) => d.xValue).left;

interface TooltipSeriesMeta {
  label: string;
  color: string;
  baseline?: number | ((point: BaseDataPoint, index: number) => number);
  tooltipRange?: boolean;
}

const resolveBaselineValue = (meta: TooltipSeriesMeta, point: BaseDataPoint, index: number): number | undefined => {
  if (!meta.tooltipRange || meta.baseline === undefined) return undefined;
  const bound = typeof meta.baseline === "function" ? meta.baseline(point, index) : meta.baseline;
  return typeof bound === "number" && Number.isFinite(bound) && bound !== point.yValue ? bound : undefined;
};

export interface FindNearestTooltipArgs {
  mouseX: number;
  datasets: BaseDataPoint[][];
  seriesMetadata: TooltipSeriesMeta[];
  seriesTypes: ("area" | "line" | "bar" | "point")[];
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  plotLeft: number;
  plotRight: number;
}

/**
 * Bisector hit-test: given a pointer x (in chart-local pixels), find the nearest
 * sample across every series and assemble the `TooltipData` to display, mirroring
 * `ChartContainer`'s camera-mode `handleMouseMove`. Pure — returns `null` when no
 * series has data near the cursor.
 */
export function findNearestTooltipData(args: FindNearestTooltipArgs): TooltipData | null {
  const { mouseX, datasets, seriesMetadata, seriesTypes, xScale, yScale, plotLeft, plotRight } = args;

  const xValue = xScale.invert(mouseX);

  const seriesData = datasets
    .map((data, datasetIndex) => {
      if (!data || data.length === 0) return null;

      const idx = bisectDate(data, xValue, 1);
      if (idx === 0) return { point: data[0], index: 0, datasetIndex };
      if (idx >= data.length) return { point: data[data.length - 1], index: data.length - 1, datasetIndex };

      const d0 = data[idx - 1];
      const d1 = data[idx];
      if (!d0 || !d1) return null;

      const distanceToD0 = Math.abs(d0.xValue.getTime() - xValue.getTime());
      const distanceToD1 = Math.abs(d1.xValue.getTime() - xValue.getTime());
      const closer0 = distanceToD0 < distanceToD1;

      return { point: closer0 ? d0 : d1, index: closer0 ? idx - 1 : idx, datasetIndex };
    })
    .filter(
      (item): item is { point: BaseDataPoint; index: number; datasetIndex: number } =>
        item !== null && seriesMetadata[item.datasetIndex] !== undefined
    )
    .map(({ point, index, datasetIndex }) => ({
      label: seriesMetadata[datasetIndex].label,
      value: point.yValue,
      baselineValue: resolveBaselineValue(seriesMetadata[datasetIndex], point, index),
      color: seriesMetadata[datasetIndex].color,
      point,
      type: seriesTypes[datasetIndex],
    }));

  if (seriesData.length === 0) return null;

  const tooltipX = xScale(seriesData[0].point.xValue);
  const shouldShowLeft = tooltipX > (plotLeft + plotRight) / 2;

  return {
    xValue: seriesData[0].point.xValue,
    series: seriesData.map(({ label, value, baselineValue, color, point, type }) => ({
      label,
      value,
      xValue: point.xValue,
      ...(baselineValue !== undefined ? { baselineValue } : {}),
      chartY: point.chartYValue ?? value,
      showMarker: point.showTooltipMarker ?? true,
      color,
      type,
      ...(point.category !== undefined ? { category: point.category } : {}),
      ...(point.extraTooltipRows && point.extraTooltipRows.length > 0
        ? { extraTooltipRows: point.extraTooltipRows }
        : {}),
    })),
    x: tooltipX,
    y: yScale(seriesData[0].point.chartYValue ?? seriesData[0].value),
    position: shouldShowLeft ? "left" : "right",
  };
}
